import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type FileUIPart, type UIMessage } from 'ai';
import Cookies from 'js-cookie';
import { ArrowUp, ChevronDown, ImagePlus, Loader2, RotateCcw, Sparkles, Square, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CitationViewer } from '@/components/agent-chat/citations/CitationViewer';
import { useOptionalAgentChat } from '@/components/agent-chat/context/AgentChatContext';
import { MessageContentViewer } from '@/components/agent-chat/messages/MessageContentViewer';
import { Citations } from '@/components/agent-chat/messages/tiptap-extensions/Citations';
import {
  Conversation,
  ConversationContent,
} from '@/components/ai-elements/conversation';
import { Message, MessageContent, MessageResponse } from '@/components/ai-elements/message';
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning';
import { useOptionalEditorContext } from '@/components/demands-v2/editor/context/editor-provider';
import { AgentChatPlugin } from '@/components/demands-v2/editor/plugins/agent-chat-plugin/agent-chat-plugin';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import axiosInstance from '@/services/axiosConfig';

export interface AgentChatV2ThreadState {
  messages: UIMessage[];
  activeRunId: string | null;
  activeMessageId: string | null;
}

function buildBaseUrl(matterId: string, threadId: string): string {
  return `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/matters/${matterId}/finch-chat/vercel/threads/${threadId}`;
}

const AGENT_CHAT_V2_THINKING_MESSAGES = [
  'Discombobulating context',
  'Speedrunning bureaucracy',
  'Summoning case law vibes',
  'Politely arguing with entropy',
  'Wrangling tool-call chaos',
  'Cross-referencing the universe',
  'Untangling claimant spaghetti',
  'Cooking up a clean response',
  'Amit-level shipping velocity engaged',
  'Moyez is polishing the edge cases',
  'Bini is taming the data gremlins',
  'Sid is refactoring reality',
  'Faris is debugging spacetime',
  'Dean is approving the chaos',
  'Ben is optimizing the hot path',
  'Mitul is stress-testing the plan',
  'Armando is on-call for mayhem',
];

interface AgentChatV2CardProps {
  matterId: string;
  threadId: string;
  isInitializing: boolean;
  initializationError: string | null;
  initialState: AgentChatV2ThreadState;
  onRefreshThreadState: () => Promise<AgentChatV2ThreadState>;
  onCancelRun: (runId: string) => Promise<AgentChatV2ThreadState>;
  onForceReset: () => Promise<AgentChatV2ThreadState>;
  onToolCall?: (toolName: string, input: unknown, output?: unknown) => void;
  onFirstMessage?: () => void;
}

type AgentChatV2MessagePart = UIMessage['parts'][number];
type AgentChatV2ToolPart = AgentChatV2MessagePart & {
  type: string;
  state?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  error_text?: string;
  toolName?: string;
  tool_name?: string;
  toolCallId?: string;
  tool_call_id?: string;
};
interface PendingImageAttachment {
  id: string;
  file: File;
  previewUrl: string;
}

const CITE_TAG_REGEX = /<cite\b[^>]*>/gi;
const CITE_CLOSE_TAG_REGEX = /<\/cite>/gi;

function stripCitationTags(text: string): string {
  return text.replace(CITE_TAG_REGEX, '').replace(CITE_CLOSE_TAG_REGEX, '');
}

function fileToFileUIPart(file: File): Promise<FileUIPart> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve({
        type: 'file',
        mediaType: file.type,
        filename: file.name,
        url: event.target?.result as string,
      });
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

function isToolPart(part: AgentChatV2MessagePart): part is AgentChatV2ToolPart {
  const { type } = part as { type?: unknown };
  return typeof type === 'string' && (type === 'dynamic-tool' || type.startsWith('tool-'));
}

function getFirstStringValue(
  record: Record<string, unknown>,
  keys: string[],
): string | undefined {
  const foundKey = keys.find((key) => {
    const value = record[key];
    return typeof value === 'string' && value.length > 0;
  });
  if (!foundKey) return undefined;
  return record[foundKey] as string;
}

function getFirstNumberValue(
  record: Record<string, unknown>,
  keys: string[],
): number | undefined {
  const values = keys.map((key) => record[key]);
  const parsedValue = values
    .map((value) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
      return undefined;
    })
    .find((value) => value !== undefined);
  return parsedValue;
}

function getToolPartName(part: AgentChatV2MessagePart): string | undefined {
  const record = part as unknown as Record<string, unknown>;

  if (part.type === 'dynamic-tool') {
    const dynamicName = getFirstStringValue(record, ['toolName', 'tool_name', 'name', 'title']);
    if (dynamicName) return dynamicName;
  }
  if (part.type.startsWith('tool-')) {
    return part.type.slice('tool-'.length);
  }
  return getFirstStringValue(record, ['toolName', 'tool_name', 'name', 'title']);
}

function parseJsonString(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeUnknown(value: unknown): unknown {
  if (typeof value === 'string') {
    return parseJsonString(value);
  }
  return value;
}

function toRecord(value: unknown): Record<string, unknown> {
  const normalizedValue = normalizeUnknown(value);

  if (!normalizedValue) {
    return {};
  }

  if (typeof normalizedValue === 'object' && !Array.isArray(normalizedValue)) {
    return normalizedValue as Record<string, unknown>;
  }

  if (Array.isArray(normalizedValue)) {
    return { items: normalizedValue };
  }

  return { value: normalizedValue };
}

type ListedFilePreview = {
  fileId: string;
  fileName: string;
  description?: string | null;
};

type ListedTaskPreview = {
  taskId: string;
  taskName: string;
  description?: string | null;
  status?: string | null;
  taskType?: string | null;
  taskGroup?: string | null;
  dueDate?: string | null;
  assignee?: string | null;
};

type HumanReviewSchemaProperty = {
  type?: string;
  format?: string;
  description?: string;
  enum?: string[];
  default?: unknown;
};

type HumanReviewSchema = {
  type?: string;
  properties?: Record<string, HumanReviewSchemaProperty>;
  required?: string[];
};

type HumanReviewConfig = {
  response_schema: HumanReviewSchema | null;
  form_title: string | null;
  form_description: string | null;
};

type SlackDestination = {
  channelName: string | null;
  channelId: string | null;
  status: string | null;
  message: string | null;
};

function parseListedFilesFromText(content: string): ListedFilePreview[] {
  return content
    .split('\n')
    .map((line) => line.replace(/^\s*[-*]\s+/, '').trim())
    .filter(Boolean)
    .reduce<ListedFilePreview[]>((acc, line) => {
      const match = line.match(/^\[(item-\d+)\]\s*[:-]?\s*(.+?)(?:\s+-\s+(.*))?$/);
      if (!match) return acc;
      const [, fileId, fileName, description] = match;
      acc.push({ fileId, fileName, description: description || null });
      return acc;
    }, []);
}

function parseListedFilesFromArray(payload: unknown[]): ListedFilePreview[] {
  return payload.reduce<ListedFilePreview[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const record = item as Record<string, unknown>;
    const fileId = getFirstStringValue(record, [
      'file_id',
      'fileId',
      'id',
      'artifact_id',
      'artifactId',
    ]);
    const fileName = getFirstStringValue(record, [
      'file_name',
      'fileName',
      'name',
      'filename',
      'title',
    ]);
    const description = getFirstStringValue(record, [
      'description',
      'summary',
      'details',
    ]);
    if (!fileId || !fileName) return acc;
    acc.push({ fileId, fileName, description: description ?? null });
    return acc;
  }, []);
}

function parseListedFilesFromUnknown(payload: unknown): ListedFilePreview[] {
  const normalizedPayload = normalizeUnknown(payload);
  if (typeof normalizedPayload === 'string') {
    return parseListedFilesFromText(normalizedPayload);
  }

  if (Array.isArray(normalizedPayload)) {
    return parseListedFilesFromArray(normalizedPayload);
  }

  if (normalizedPayload && typeof normalizedPayload === 'object') {
    const record = normalizedPayload as Record<string, unknown>;
    const directFile = parseListedFilesFromArray([record]);
    if (directFile.length > 0) return directFile;

    const nestedCandidates = [
      record.files,
      record.items,
      record.results,
      record.return_value,
      record.data,
      record.output,
      record.value,
    ];
    const nestedFiles = nestedCandidates
      .map((candidate) => parseListedFilesFromUnknown(candidate))
      .find((candidate) => candidate.length > 0);
    if (nestedFiles) return nestedFiles;
  }

  return [];
}

function parseListedTasksFromArray(payload: unknown[]): ListedTaskPreview[] {
  return payload.reduce<ListedTaskPreview[]>((acc, item) => {
    if (!item || typeof item !== 'object') return acc;
    const record = item as Record<string, unknown>;
    const taskId = getFirstStringValue(record, ['id', 'task_id', 'taskId']);
    const taskName = getFirstStringValue(record, ['name', 'task_name', 'title']);
    const description = getFirstStringValue(record, ['description']);
    const status = getFirstStringValue(record, ['status', 'canonical_status']);
    const taskType = getFirstStringValue(record, ['type', 'canonical_type']);
    const taskGroup = getFirstStringValue(record, ['group', 'canonical_group']);
    const dueDate = getFirstStringValue(record, ['due_date', 'dueDate']);
    const assignee = getFirstStringValue(record, ['assignee']);
    if (!taskId || !taskName) return acc;
    acc.push({
      taskId,
      taskName,
      description: description ?? null,
      status: status ?? null,
      taskType: taskType ?? null,
      taskGroup: taskGroup ?? null,
      dueDate: dueDate ?? null,
      assignee: assignee ?? null,
    });
    return acc;
  }, []);
}

function parseListedTasksFromUnknown(payload: unknown): ListedTaskPreview[] {
  const normalizedPayload = normalizeUnknown(payload);
  if (Array.isArray(normalizedPayload)) {
    return parseListedTasksFromArray(normalizedPayload);
  }

  if (normalizedPayload && typeof normalizedPayload === 'object') {
    const record = normalizedPayload as Record<string, unknown>;
    const directTask = parseListedTasksFromArray([record]);
    if (directTask.length > 0) return directTask;

    const nestedCandidates = [
      record.tasks,
      record.items,
      record.results,
      record.data,
      record.output,
      record.value,
    ];
    const nestedTasks = nestedCandidates
      .map((candidate) => parseListedTasksFromUnknown(candidate))
      .find((candidate) => candidate.length > 0);
    if (nestedTasks) return nestedTasks;
  }

  return [];
}

function buildHumanReviewApprovalIdFromToolCallId(toolCallId: string): string {
  const normalized = toolCallId.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  const suffix = normalized.length > 0 ? normalized.slice(0, 48) : 'unknown';
  return `finch-review-${suffix}`;
}

function toHumanReviewSchema(value: unknown): HumanReviewSchema | null {
  const record = toRecord(value);
  const { properties } = record;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
    return null;
  }
  return record as unknown as HumanReviewSchema;
}

function extractHumanReviewConfig(args: Record<string, unknown>, output: unknown): HumanReviewConfig {
  const outputRecord = toRecord(output);
  const outputSchema = toHumanReviewSchema(outputRecord.response_schema);
  const argSchema = toHumanReviewSchema(args.response_schema);

  return {
    response_schema: outputSchema || argSchema,
    form_title:
      getFirstStringValue(outputRecord, ['form_title'])
      || getFirstStringValue(args, ['form_title'])
      || null,
    form_description:
      getFirstStringValue(outputRecord, ['form_description'])
      || getFirstStringValue(args, ['form_description', 'requested_action'])
      || null,
  };
}

function getDefaultFormValues(schema: HumanReviewSchema | null): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  const properties = schema?.properties || {};
  Object.entries(properties).forEach(([key, property]) => {
    if (property.default !== undefined) {
      defaults[key] = property.default;
    } else if (property.type === 'boolean') {
      defaults[key] = false;
    } else if (property.type === 'array') {
      defaults[key] = [];
    } else {
      defaults[key] = '';
    }
  });
  return defaults;
}

function formatFieldLabel(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase());
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function parseSubmittedReviewData(output: unknown): Record<string, unknown> {
  const record = toRecord(output);
  const modifiedData = toRecord(record.modified_data);
  if (Object.keys(modifiedData).length > 0) {
    return modifiedData;
  }
  return {};
}

function parseSlackDestination(output: unknown, args: Record<string, unknown>): SlackDestination {
  const record = toRecord(output);
  const nestedResult = toRecord(record.result);
  const status = (
    getFirstStringValue(record, ['status'])
    || getFirstStringValue(nestedResult, ['status'])
    || null
  );
  const message = (
    getFirstStringValue(record, ['message'])
    || getFirstStringValue(nestedResult, ['message'])
    || null
  );
  const explicitChannelName = (
    getFirstStringValue(record, ['channel_name', 'channel'])
    || getFirstStringValue(nestedResult, ['channel_name', 'channel'])
  );
  const explicitChannelId = (
    getFirstStringValue(record, ['channel_id'])
    || getFirstStringValue(nestedResult, ['channel_id'])
  );

  const argChannel = getFirstStringValue(args, ['channel']);
  const parsedChannelName = message?.match(/to\s+([a-zA-Z0-9_-]+)\s+channel/i)?.[1];
  const parsedChannelId = message?.match(/\((C[A-Z0-9]+)\)/)?.[1]
    || message?.match(/channel=([A-Z0-9]+)/)?.[1];

  return {
    channelName: explicitChannelName || argChannel || parsedChannelName || null,
    channelId: explicitChannelId || parsedChannelId || null,
    status,
    message,
  };
}

function normalizeToolArgs(
  toolName: string | undefined,
  input: unknown,
  fileNamesByShortId: Record<string, string>,
): Record<string, unknown> {
  const normalized = toRecord(input);
  if (
    (toolName === 'read_file' || toolName === 'read_file_or_transcript')
    && typeof normalized.explanation !== 'string'
  ) {
    normalized.explanation = '';
  }

  if (toolName === 'read_file' || toolName === 'read_file_or_transcript') {
    const fileId = getFirstStringValue(normalized, [
      'file_id',
      'target_file',
      'target_id',
    ]);
    if (fileId && typeof fileNamesByShortId[fileId] === 'string') {
      normalized.file_name = fileNamesByShortId[fileId];
    }
  }

  return normalized;
}

type V2ToolBadgeProps = {
  toolName: string;
  args: Record<string, unknown>;
  output: unknown;
  errorText: string;
  isLoading: boolean;
  isDone: boolean;
  isError: boolean;
  fileNamesByShortId: Record<string, string>;
};

const LIST_TOOL_NAMES = new Set([
  'list_files',
  'list_files_and_transcripts',
  'list_files_transcripts_and_texts',
]);
const TASK_LIST_TOOL_NAMES = new Set(['list_tasks']);
const SLACK_TOOL_NAMES = new Set(['send_slack_message', 'report_issue_to_slack']);

function formatToolName(toolName: string): string {
  return toolName
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const V2ToolBadge = ({
  toolName,
  args,
  output,
  errorText,
  isLoading,
  isDone,
  isError,
  fileNamesByShortId,
}: V2ToolBadgeProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isListTool = LIST_TOOL_NAMES.has(toolName);
  const isTaskListTool = TASK_LIST_TOOL_NAMES.has(toolName);
  const isSlackTool = SLACK_TOOL_NAMES.has(toolName);
  const isReadTool = toolName === 'read_file' || toolName === 'read_file_or_transcript';
  const files = useMemo(
    () => (isListTool ? parseListedFilesFromUnknown(output) : []),
    [isListTool, output],
  );
  const tasks = useMemo(
    () => (isTaskListTool ? parseListedTasksFromUnknown(output) : []),
    [isTaskListTool, output],
  );
  const taskStatusCounts = useMemo(
    () =>
      tasks.reduce<Record<string, number>>((acc, task) => {
        const status = task.status || 'Unknown';
        return {
          ...acc,
          [status]: (acc[status] || 0) + 1,
        };
      }, {}),
    [tasks],
  );
  const slackDestination = useMemo(
    () => (isSlackTool ? parseSlackDestination(output, args) : null),
    [args, isSlackTool, output],
  );

  const readTarget = useMemo(() => {
    if (!isReadTool) return null;
    const fileId = getFirstStringValue(args, ['file_id', 'target_file', 'target_id']);
    const fromArgs = getFirstStringValue(args, ['file_name', 'fileName']);
    const fromMap = fileId ? fileNamesByShortId[fileId] : undefined;
    const label = fromArgs || fromMap || fileId || 'file';
    return { label, fileId };
  }, [args, fileNamesByShortId, isReadTool]);

  const statusText = useMemo(() => {
    if (isError) {
      if (isReadTool && readTarget) return `Error reading ${readTarget.label}`;
      if (isListTool) return 'Error listing files';
      if (isTaskListTool) return 'Error listing tasks';
      if (isSlackTool) return 'Error sending Slack message';
      return `Error using ${formatToolName(toolName)}`;
    }
    if (isLoading) {
      if (isReadTool) return 'Reading file...';
      if (isListTool) return 'Listing files...';
      if (isTaskListTool) return 'Listing tasks...';
      if (isSlackTool) return 'Sending Slack message...';
      return `Using ${formatToolName(toolName)}...`;
    }
    if (isDone) {
      if (isReadTool && readTarget) return `Read ${readTarget.label}`;
      if (isListTool) return files.length > 0 ? `Found ${files.length} files` : 'No files found';
      if (isTaskListTool) return tasks.length > 0 ? `Found ${tasks.length} tasks` : 'No tasks found';
      if (isSlackTool) {
        if (slackDestination?.channelName) {
          return `Sent to #${slackDestination.channelName}`;
        }
        if (slackDestination?.channelId) {
          return `Sent to ${slackDestination.channelId}`;
        }
        return 'Sent Slack message';
      }
      return `Used ${formatToolName(toolName)}`;
    }
    return `Using ${formatToolName(toolName)}...`;
  }, [
    files.length,
    isDone,
    isError,
    isListTool,
    isLoading,
    isReadTool,
    isSlackTool,
    isTaskListTool,
    readTarget,
    slackDestination,
    tasks.length,
    toolName,
  ]);

  const hasArgs = Object.keys(args).length > 0;
  const canExpand = files.length > 0
    || tasks.length > 0
    || isSlackTool
    || hasArgs
    || (isError && errorText.length > 0);

  return (
    <div
      className={cn(
        'rounded-md border overflow-hidden',
        isError && 'border-red-200 bg-red-50/40',
        isLoading && 'border-zinc-200 bg-zinc-50/70',
        isDone && !isError && 'border-emerald-200 bg-emerald-50/50',
      )}
    >
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => {
          if (canExpand) setIsExpanded((prev) => !prev);
        }}
        className={cn(
          'w-full px-2 py-1.5 text-left text-[11px] flex items-center gap-2',
          canExpand && 'hover:bg-black/[0.02]',
        )}
      >
        <span className="flex-1 truncate">{statusText}</span>
        {canExpand && (
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 text-zinc-400 transition-transform duration-150',
              isExpanded && 'rotate-180',
            )}
          />
        )}
      </button>

      {canExpand && isExpanded && (
        <div className="border-t border-zinc-200/70 px-2 py-1.5">
          {isSlackTool && slackDestination && (
            <div className="mb-2 rounded border border-zinc-200 bg-white/70 p-1.5">
              <p className="text-[10px] text-zinc-700">
                Channel: {slackDestination.channelName || 'Unknown'}
                {slackDestination.channelId ? ` (${slackDestination.channelId})` : ''}
              </p>
              {slackDestination.status && (
                <p className="text-[10px] text-zinc-500">Status: {slackDestination.status}</p>
              )}
              {slackDestination.message && (
                <p className="text-[10px] text-zinc-500 line-clamp-2">{slackDestination.message}</p>
              )}
            </div>
          )}

          {tasks.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-1">
                {Object.entries(taskStatusCounts).map(([status, count]) => (
                  <span
                    key={status}
                    className="rounded-full border border-zinc-200 bg-white/80 px-1.5 py-0.5 text-[10px] text-zinc-600"
                  >
                    {count} {status}
                  </span>
                ))}
              </div>
              <div className="max-h-72 overflow-y-auto space-y-1">
                {tasks.map((task) => (
                  <div key={task.taskId} className="rounded border border-zinc-200 bg-white/80 p-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] text-zinc-800">{task.taskName}</p>
                      {task.status && (
                        <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-600">
                          {task.status}
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-[10px] text-zinc-500 line-clamp-2">{task.description}</p>
                    )}
                    <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-zinc-500">
                      {task.taskGroup && <span className="rounded bg-zinc-100 px-1 py-0.5">{task.taskGroup}</span>}
                      {task.taskType && <span className="rounded bg-zinc-100 px-1 py-0.5">{task.taskType}</span>}
                      {task.dueDate && <span>Due {task.dueDate}</span>}
                      {task.assignee && <span>Owner {task.assignee}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div className="max-h-64 overflow-y-auto space-y-1">
              {files.map((file) => (
                <div key={`${file.fileId}-${file.fileName}`} className="rounded border border-zinc-200 bg-white/70 p-1.5">
                  <p className="text-[11px] text-zinc-800">{file.fileName}</p>
                  <p className="text-[10px] text-zinc-500">{file.description || `File ID: ${file.fileId}`}</p>
                </div>
              ))}
            </div>
          )}

          {files.length === 0 && tasks.length === 0 && hasArgs && (
            <div className="space-y-1">
              {Object.entries(args).map(([key, value]) => (
                <div key={key} className="text-[10px] text-zinc-600">
                  <span className="text-zinc-400">{key}:</span>{' '}
                  {typeof value === 'string' ? value : JSON.stringify(value)}
                </div>
              ))}
            </div>
          )}

          {isError && errorText.length > 0 && (
            <p className="text-[10px] text-red-600">{errorText}</p>
          )}
        </div>
      )}
    </div>
  );
};

type HumanReviewToolBadgeProps = {
  matterId: string;
  toolCallId?: string;
  args: Record<string, unknown>;
  output: unknown;
  errorText: string;
  isLoading: boolean;
  isDone: boolean;
  isError: boolean;
  tasksById: Record<string, ListedTaskPreview>;
};

const HumanReviewToolBadge = ({
  matterId,
  toolCallId,
  args,
  output,
  errorText,
  isLoading,
  isDone,
  isError,
  tasksById,
}: HumanReviewToolBadgeProps) => {
  const [formConfig, setFormConfig] = useState<HumanReviewConfig>(() =>
    extractHumanReviewConfig(args, output),
  );
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submittedData = useMemo(() => parseSubmittedReviewData(output), [output]);
  const outputRecord = useMemo(() => toRecord(output), [output]);
  const hasPersistedSubmission = Object.keys(submittedData).length > 0;
  const approvalId = useMemo(() => {
    const outputApprovalId = getFirstStringValue(outputRecord, ['approval_id']);
    if (outputApprovalId) return outputApprovalId;
    const argApprovalId = getFirstStringValue(args, ['approval_id']);
    if (argApprovalId) return argApprovalId;
    if (!toolCallId) return null;
    return buildHumanReviewApprovalIdFromToolCallId(toolCallId);
  }, [args, outputRecord, toolCallId]);
  const timeoutHours = useMemo(() => {
    const outputTimeout = getFirstNumberValue(outputRecord, ['timeout_hours']);
    if (outputTimeout !== undefined) return Math.max(1, Math.round(outputTimeout));
    const argTimeout = getFirstNumberValue(args, ['timeout_hours']);
    if (argTimeout !== undefined) return Math.max(1, Math.round(argTimeout));
    return 24;
  }, [args, outputRecord]);

  const taskOptions = useMemo(
    () => Object.values(tasksById).sort((a, b) => a.taskName.localeCompare(b.taskName)),
    [tasksById],
  );

  useEffect(() => {
    const nextConfig = extractHumanReviewConfig(args, output);
    setFormConfig((previous) => ({
      response_schema: nextConfig.response_schema || previous.response_schema,
      form_title: nextConfig.form_title || previous.form_title,
      form_description: nextConfig.form_description || previous.form_description,
    }));
  }, [args, output]);

  useEffect(() => {
    if (formConfig.response_schema || !approvalId) {
      return undefined;
    }

    let cancelled = false;
    axiosInstance
      .get(`/api/agentflow/eval/${matterId}/${approvalId}/`)
      .then((response) => {
        if (cancelled) return;
        const data = toRecord(response.data);
        setFormConfig((previous) => ({
          response_schema:
            toHumanReviewSchema(data.response_schema) || previous.response_schema,
          form_title: getFirstStringValue(data, ['form_title']) || previous.form_title,
          form_description:
            getFirstStringValue(data, ['form_description']) || previous.form_description,
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setSubmitError('Unable to load human review form configuration.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [approvalId, formConfig.response_schema, matterId]);

  useEffect(() => {
    if (!formConfig.response_schema) {
      return;
    }
    setFormData((previous) => {
      if (Object.keys(previous).length > 0) {
        return previous;
      }
      return getDefaultFormValues(formConfig.response_schema);
    });
  }, [formConfig.response_schema]);

  const properties = formConfig.response_schema?.properties || {};
  const requiredFields = new Set(formConfig.response_schema?.required || []);
  const action = getFirstStringValue(outputRecord, ['action']);
  const fallbackNotesId = `${approvalId || 'review'}-notes`;
  let statusLabel = 'pending';
  if (hasPersistedSubmission || hasSubmitted) {
    statusLabel = 'submitted';
  } else if (isLoading) {
    statusLabel = 'waiting for reviewer';
  } else if (isDone) {
    statusLabel = 'completed';
  }

  const updateField = (field: string, value: unknown) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!approvalId || isSubmitting || hasPersistedSubmission) {
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/agentflow/eval/${matterId}/${approvalId}/submit/`, {
        action: 'submit',
        modified_data: formData,
      });
      setHasSubmitted(true);
    } catch {
      setSubmitError('Failed to submit human review.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn(
      'rounded-md border px-2.5 py-2 space-y-2',
      isError ? 'border-red-200 bg-red-50/40' : 'border-blue-200 bg-blue-50/45',
    )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-zinc-800">
          {formConfig.form_title || 'Human Review'}
        </p>
        <span className="text-[10px] text-zinc-500">{statusLabel}</span>
      </div>

      {formConfig.form_description && (
        <p className="text-[10px] text-zinc-600">{formConfig.form_description}</p>
      )}

      {approvalId && (
        <p className="text-[10px] text-zinc-500">Review ID: {approvalId}</p>
      )}
      <p className="text-[10px] text-zinc-500">
        Timeout: {timeoutHours} hour{timeoutHours === 1 ? '' : 's'}
      </p>

      {hasPersistedSubmission ? (
        <div className="space-y-1 rounded border border-emerald-200 bg-emerald-50/60 p-2">
          <p className="text-[10px] font-medium text-emerald-800">
            Human submitted response{action ? ` (${action})` : ''}
          </p>
          {Object.entries(submittedData).map(([key, value]) => (
            <div key={key} className="text-[10px] text-emerald-900">
              <span className="text-emerald-700">{formatFieldLabel(key)}:</span>{' '}
              {stringifyValue(value)}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {Object.keys(properties).length > 0 ? (
            Object.entries(properties).map(([field, property]) => {
              const label = property.description || formatFieldLabel(field);
              const value = formData[field];
              const isRequired = requiredFields.has(field);
              const controlId = `${approvalId || 'review'}-${field}`;
              const lowerField = field.toLowerCase();
              const isTaskField =
                (field === 'task_id' || lowerField.endsWith('_task_id'))
                && taskOptions.length > 0;
              const isDateField =
                property.format === 'date'
                || property.format === 'date-time'
                || lowerField.endsWith('_date')
                || lowerField.includes('date');
              const isLongText =
                property.type === 'string'
                && (
                  lowerField.includes('notes')
                  || lowerField.includes('instruction')
                  || lowerField.includes('reason')
                  || lowerField.includes('description')
                );

              if (property.enum && property.enum.length > 0) {
                return (
                  <div key={field}>
                    <label htmlFor={controlId} className="block text-[10px] text-zinc-700 mb-1">
                      {label}{isRequired ? ' *' : ''}
                    </label>
                    <select
                      id={controlId}
                      className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                      value={typeof value === 'string' ? value : ''}
                      onChange={(event) => updateField(field, event.target.value)}
                      disabled={isSubmitting || hasSubmitted || isDone}
                    >
                      <option value="">Select</option>
                      {property.enum.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (isTaskField) {
                return (
                  <div key={field}>
                    <label htmlFor={controlId} className="block text-[10px] text-zinc-700 mb-1">
                      {label}{isRequired ? ' *' : ''}
                    </label>
                    <select
                      id={controlId}
                      className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                      value={typeof value === 'string' ? value : ''}
                      onChange={(event) => updateField(field, event.target.value)}
                      disabled={isSubmitting || hasSubmitted || isDone}
                    >
                      <option value="">Select a task</option>
                      {taskOptions.map((task) => (
                        <option key={task.taskId} value={task.taskId}>
                          {task.taskName}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }

              if (property.type === 'boolean') {
                return (
                  <div key={field} className="flex items-center gap-2 text-[11px] text-zinc-700">
                    <input
                      id={controlId}
                      type="checkbox"
                      checked={Boolean(value)}
                      onChange={(event) => updateField(field, event.target.checked)}
                      disabled={isSubmitting || hasSubmitted || isDone}
                    />
                    <label htmlFor={controlId}>{label}</label>
                  </div>
                );
              }

              if (property.type === 'number' || property.type === 'integer') {
                return (
                  <div key={field}>
                    <label htmlFor={controlId} className="block text-[10px] text-zinc-700 mb-1">
                      {label}{isRequired ? ' *' : ''}
                    </label>
                    <input
                      id={controlId}
                      type="number"
                      className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                      value={typeof value === 'number' ? value : ''}
                      onChange={(event) =>
                        updateField(field, event.target.value === '' ? '' : Number(event.target.value))
                      }
                      disabled={isSubmitting || hasSubmitted || isDone}
                    />
                  </div>
                );
              }

              if (isDateField) {
                return (
                  <div key={field}>
                    <label htmlFor={controlId} className="block text-[10px] text-zinc-700 mb-1">
                      {label}{isRequired ? ' *' : ''}
                    </label>
                    <input
                      id={controlId}
                      type={property.format === 'date-time' ? 'datetime-local' : 'date'}
                      className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                      value={typeof value === 'string' ? value : ''}
                      onChange={(event) => updateField(field, event.target.value)}
                      disabled={isSubmitting || hasSubmitted || isDone}
                    />
                  </div>
                );
              }

              if (isLongText) {
                return (
                  <div key={field}>
                    <label htmlFor={controlId} className="block text-[10px] text-zinc-700 mb-1">
                      {label}{isRequired ? ' *' : ''}
                    </label>
                    <textarea
                      id={controlId}
                      rows={3}
                      className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                      value={typeof value === 'string' ? value : ''}
                      onChange={(event) => updateField(field, event.target.value)}
                      disabled={isSubmitting || hasSubmitted || isDone}
                    />
                  </div>
                );
              }

              return (
                <div key={field}>
                  <label htmlFor={controlId} className="block text-[10px] text-zinc-700 mb-1">
                    {label}{isRequired ? ' *' : ''}
                  </label>
                  <input
                    id={controlId}
                    type="text"
                    className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                    value={typeof value === 'string' ? value : stringifyValue(value)}
                    onChange={(event) => updateField(field, event.target.value)}
                    disabled={isSubmitting || hasSubmitted || isDone}
                  />
                </div>
              );
            })
          ) : (
            <div>
              <p className="block text-[10px] text-zinc-700 mb-1">Notes</p>
              <textarea
                id={fallbackNotesId}
                aria-label="Human review notes"
                rows={3}
                className="w-full rounded border border-zinc-300 bg-white px-2 py-1.5 text-[11px]"
                value={typeof formData.notes === 'string' ? formData.notes : ''}
                onChange={(event) => updateField('notes', event.target.value)}
                disabled={isSubmitting || hasSubmitted || isDone}
              />
            </div>
          )}

          {!hasSubmitted && !isDone && (
            <button
              type="button"
              onClick={() => {
                handleSubmit().catch(() => undefined);
              }}
              disabled={!approvalId || isSubmitting}
              className={cn(
                'rounded bg-blue-600 px-2.5 py-1 text-[11px] font-medium text-white',
                (!approvalId || isSubmitting) && 'opacity-60',
              )}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Review'}
            </button>
          )}

          {hasSubmitted && !hasPersistedSubmission && (
            <p className="text-[10px] text-blue-700">Submitted. Waiting for the model to resume...</p>
          )}
        </div>
      )}

      {(submitError || (isError && errorText)) && (
        <p className="text-[10px] text-red-600">{submitError || errorText}</p>
      )}
    </div>
  );
};

function renderToolPart(
  part: AgentChatV2MessagePart,
  isStreaming: boolean,
  matterId: string,
  fileNamesByShortId: Record<string, string>,
  tasksById: Record<string, ListedTaskPreview>,
): React.ReactNode {
  if (!isToolPart(part)) {
    return null;
  }

  const toolName = getToolPartName(part);
  const input = 'input' in part ? normalizeUnknown(part.input) : undefined;
  const args = normalizeToolArgs(toolName, input, fileNamesByShortId);
  const output = 'output' in part ? normalizeUnknown(part.output) : undefined;
  const partRecord = part as unknown as Record<string, unknown>;
  const errorText = getFirstStringValue(partRecord, ['errorText', 'error_text']) ?? '';
  const toolCallId = getFirstStringValue(partRecord, ['toolCallId', 'tool_call_id', 'id']);
  const isError = part.state === 'output-error' || part.state === 'output-denied';
  const isDone = part.state === 'output-available' || isError;
  const isLoading = !isDone && isStreaming;

  if (toolName === 'escalate_for_human_review') {
    return (
      <div className="mb-2">
        <HumanReviewToolBadge
          matterId={matterId}
          toolCallId={toolCallId}
          args={args}
          output={output}
          errorText={errorText}
          isLoading={isLoading}
          isDone={isDone}
          isError={isError}
          tasksById={tasksById}
        />
      </div>
    );
  }

  return (
    <div className="mb-2">
      <V2ToolBadge
        toolName={toolName ?? 'tool'}
        args={args}
        output={output}
        errorText={errorText}
        isLoading={isLoading}
        isDone={isDone}
        isError={isError}
        fileNamesByShortId={fileNamesByShortId}
      />
    </div>
  );
}

function renderFilePart(part: AgentChatV2MessagePart, key: string): React.ReactNode {
  if (part.type !== 'file') {
    return null;
  }

  const record = part as unknown as Record<string, unknown>;
  // Accept both camelCase (from streaming/AI SDK) and snake_case (from session reload).
  const mediaType =
    typeof record.mediaType === 'string'
      ? record.mediaType
      : typeof record.media_type === 'string'
        ? record.media_type
        : '';
  const fileUrl = typeof record.url === 'string' ? record.url : '';

  if (!fileUrl) {
    return (
      <div key={key} className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-[11px] text-zinc-600">
        Attachment sent
      </div>
    );
  }

  const isImage = mediaType.startsWith('image/') || fileUrl.startsWith('data:image/');
  if (!isImage) {
    return (
      <a
        key={key}
        href={fileUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-[11px] text-zinc-700 hover:bg-zinc-100"
      >
        View attachment
      </a>
    );
  }

  return (
    <div key={key} className="mt-1 overflow-hidden rounded-lg border border-zinc-200">
      <img src={fileUrl} alt="Uploaded attachment" className="max-h-64 w-auto object-contain" />
    </div>
  );
}

export const AgentChatV2Card: React.FC<AgentChatV2CardProps> = ({
  matterId,
  threadId,
  isInitializing,
  initializationError,
  initialState,
  onRefreshThreadState,
  onCancelRun,
  onForceReset,
  onToolCall,
  onFirstMessage,
}) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(initialState.activeRunId);
  const [fileNamesByShortId, setFileNamesByShortId] = useState<Record<string, string>>({});
  const [tasksById, setTasksById] = useState<Record<string, ListedTaskPreview>>({});
  const [thinkingMessageIndex, setThinkingMessageIndex] = useState(0);
  const [pendingImages, setPendingImages] = useState<PendingImageAttachment[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingImagesRef = useRef<PendingImageAttachment[]>([]);
  const optionalAgentChat = useOptionalAgentChat();

  const editorCtx = useOptionalEditorContext();
  const editor = editorCtx?.editor ?? null;

  const threadIdRef = useRef<string>(threadId);
  const headMessageIdRef = useRef<string | null>(initialState.activeMessageId);
  const hasFiredFirstMessageRef = useRef(initialState.messages.length > 0);

  useEffect(() => {
    threadIdRef.current = threadId;
  }, [threadId]);

  useEffect(() => {
    setFileNamesByShortId({});
    setTasksById({});
  }, [threadId]);

  useEffect(() => {
    headMessageIdRef.current = initialState.activeMessageId;
    setActiveRunId(initialState.activeRunId);
  }, [initialState.activeMessageId, initialState.activeRunId]);

  useEffect(() => {
    pendingImagesRef.current = pendingImages;
  }, [pendingImages]);

  useEffect(() => {
    return () => {
      pendingImagesRef.current.forEach((attachment) => {
        URL.revokeObjectURL(attachment.previewUrl);
      });
    };
  }, []);

  const transport = useMemo(() => {
    return new DefaultChatTransport({
      api: `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/health/`,
      credentials: 'include',
      headers: {
        'X-CSRFToken': Cookies.get('csrftoken') ?? '',
      },
      prepareSendMessagesRequest: ({ id, messageId, messages: msgs, trigger }) => ({
        api: `${buildBaseUrl(matterId, threadIdRef.current)}/runs/stream/`,
        body: {
          id,
          trigger,
          messageId,
          messages: msgs,
          parent_message_id: headMessageIdRef.current,
        },
      }),
      prepareReconnectToStreamRequest: () => ({
        api: `${buildBaseUrl(matterId, threadIdRef.current)}/runs/active-stream/`,
      }),
    });
  }, [matterId]);

  const {
    clearError,
    error: chatError,
    messages,
    resumeStream,
    sendMessage,
    setMessages,
    status,
    stop,
  } = useChat({
    id: `finch-${matterId}-${threadId}`,
    transport,
    resume: Boolean(initialState.activeRunId),
    onFinish: async ({ isDisconnect }) => {
      let refreshedState: AgentChatV2ThreadState | null = null;

      try {
        refreshedState = await onRefreshThreadState();
        headMessageIdRef.current = refreshedState.activeMessageId;
        setActiveRunId(refreshedState.activeRunId);
      } catch {
        setActiveRunId(null);
      }

      if (isDisconnect && refreshedState?.activeRunId) {
        setError(null);
        clearError();
        resumeStream().catch(() => {});
        return;
      }

      if (editor) {
        const plugin = editor.plugins.get(AgentChatPlugin) as AgentChatPlugin;
        plugin.handleChatEnded().catch(() => {});
      }

      if (!hasFiredFirstMessageRef.current && onFirstMessage) {
        hasFiredFirstMessageRef.current = true;
        onFirstMessage();
      }
    },
  });

  useEffect(() => {
    setMessages(initialState.messages);
  }, [initialState.messages, setMessages]);

  const isStreaming = status === 'streaming' || status === 'submitted';

  useEffect(() => {
    let intervalId: number | null = null;

    if (!isStreaming) {
      setThinkingMessageIndex(0);
    } else {
      intervalId = window.setInterval(() => {
        setThinkingMessageIndex((prev) => (prev + 1) % AGENT_CHAT_V2_THINKING_MESSAGES.length);
      }, 1400);
    }

    return () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
      }
    };
  }, [isStreaming]);

  // Poll for proposed edits that may have been created but not yet applied
  useEffect(() => {
    if (!editor || isStreaming) return undefined;

    const intervalId = window.setInterval(() => {
      const plugin = editor.plugins.get(AgentChatPlugin) as AgentChatPlugin;
      plugin.checkForSuggestedEdits();
    }, 10_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [editor, isStreaming]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  const addImageFiles = useCallback((files: FileList | File[]) => {
    const images = Array.from(files).filter((file) => file.type.startsWith('image/'));
    if (images.length === 0) {
      return;
    }
    const next = images.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingImages((previous) => [...previous, ...next]);
  }, []);

  const removePendingImage = useCallback((id: string) => {
    setPendingImages((previous) => {
      const target = previous.find((attachment) => attachment.id === id);
      if (target) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return previous.filter((attachment) => attachment.id !== id);
    });
  }, []);

  const handleSend = useCallback(async () => {
    const prompt = input.trim();
    if ((!prompt && pendingImages.length === 0) || isStreaming || isInitializing) {
      return;
    }

    const imagesToSend = [...pendingImages];
    setInput('');
    setError(null);
    clearError();

    try {
      const fileParts =
        imagesToSend.length > 0
          ? await Promise.all(imagesToSend.map((image) => fileToFileUIPart(image.file)))
          : undefined;

      // Clear pending images immediately so the preview cards disappear on send
      setPendingImages((current) => {
        const sent = new Set(imagesToSend.map((image) => image.id));
        imagesToSend.forEach((image) => URL.revokeObjectURL(image.previewUrl));
        return current.filter((image) => !sent.has(image.id));
      });

      if (editor) {
        const plugin = editor.plugins.get(AgentChatPlugin) as AgentChatPlugin;
        plugin.handleChatStarted();
      }

      await sendMessage({
        text: prompt,
        ...(fileParts ? { files: fileParts } : {}),
      });
    } catch (sendError) {
      setInput(prompt);
      setError(sendError instanceof Error ? sendError.message : 'Failed to send message');
    }
  }, [clearError, editor, input, isInitializing, isStreaming, pendingImages, sendMessage]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend().catch(() => {});
      }
    },
    [handleSend],
  );

  const handleInputPaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const images = Array.from(event.clipboardData.files).filter((file) =>
        file.type.startsWith('image/'),
      );
      if (images.length === 0) return;
      event.preventDefault();
      addImageFiles(images);
    },
    [addImageFiles],
  );

  const [isResetting, setIsResetting] = useState(false);

  const handleForceReset = useCallback(async () => {
    setIsResetting(true);
    setError(null);
    clearError();
    stop();
    try {
      const refreshed = await onForceReset();
      headMessageIdRef.current = refreshed.activeMessageId;
      setActiveRunId(refreshed.activeRunId);
      setMessages(refreshed.messages);
      setError(null);
      clearError();
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : 'Failed to reset thread');
    } finally {
      setIsResetting(false);
    }
  }, [clearError, onForceReset, setMessages, stop]);

  const handleCancel = useCallback(async () => {
    stop();

    // Remove the last assistant message if it has no text content (just tool calls / reasoning).
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role !== 'assistant') return prev;
      const hasText = last.parts.some((p) => p.type === 'text' && p.text.trim().length > 0);
      return hasText ? prev : prev.slice(0, -1);
    });

    try {
      let runId = activeRunId;
      if (!runId) {
        const refreshed = await onRefreshThreadState();
        runId = refreshed.activeRunId;
      }

      if (!runId) {
        setActiveRunId(null);
        return;
      }

      const refreshed = await onCancelRun(runId);
      headMessageIdRef.current = refreshed.activeMessageId;
      setActiveRunId(refreshed.activeRunId);
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : 'Failed to cancel run');
    }
  }, [activeRunId, onCancelRun, onRefreshThreadState, setMessages, stop]);

  const dispatchedToolCallIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    messages
      .filter((message) => message.role === 'assistant')
      .forEach((message) => {
        message.parts
          .filter((part) => isToolPart(part) && part.state === 'output-available')
          .forEach((part) => {
            const toolCallId = 'toolCallId' in part ? (part.toolCallId as string) : undefined;
            const snakeToolCallId =
              'tool_call_id' in part ? (part.tool_call_id as string | undefined) : undefined;
            const resolvedToolCallId = toolCallId || snakeToolCallId;
            if (!resolvedToolCallId || dispatchedToolCallIds.current.has(resolvedToolCallId)) return;

            const toolName = getToolPartName(part);
            const toolInput = 'input' in part ? normalizeUnknown(part.input) : undefined;
            const toolOutput = 'output' in part ? normalizeUnknown(part.output) : undefined;

            if (
              toolName === 'list_files'
              || toolName === 'list_files_and_transcripts'
              || toolName === 'list_files_transcripts_and_texts'
            ) {
              const parsedFiles = parseListedFilesFromUnknown(toolOutput);
              if (parsedFiles.length > 0) {
                setFileNamesByShortId((current) => {
                  const entries = Object.fromEntries(
                    parsedFiles.map((file) => [file.fileId, file.fileName]),
                  );
                  const merged = { ...current, ...entries };
                  const unchanged = Object.entries(entries).every(
                    ([key, value]) => current[key] === value,
                  );
                  return unchanged ? current : merged;
                });
              }
            }

            if (toolName === 'list_tasks' || toolName === 'read_task') {
              const parsedTasks = parseListedTasksFromUnknown(toolOutput);
              if (parsedTasks.length > 0) {
                setTasksById((current) => {
                  const entries = Object.fromEntries(
                    parsedTasks.map((task) => [task.taskId, task]),
                  );
                  const merged = { ...current, ...entries };
                  const unchanged = Object.entries(entries).every(
                    ([key, value]) => current[key]?.taskName === value.taskName,
                  );
                  return unchanged ? current : merged;
                });
              }
            }

            if (toolName) {
              dispatchedToolCallIds.current.add(resolvedToolCallId);
              onToolCall?.(toolName, toolInput, toolOutput);
            }
          });
      });
  }, [messages, onToolCall]);

  const renderedMessages = messages.filter(
    (message) => message.role === 'assistant' || message.role === 'user',
  );
  const selectedCitation = optionalAgentChat?.selectedCitation ?? null;
  const setSelectedCitation = optionalAgentChat?.setSelectedCitation;

  const setSelectedCitationId = useCallback(
    (nextBlockId: string | null) => {
      if (!setSelectedCitation) return;
      if (nextBlockId === null) {
        setSelectedCitation(null);
        return;
      }
      if (!selectedCitation) return;
      setSelectedCitation({
        ...selectedCitation,
        selectedBlockId: nextBlockId,
      });
    },
    [selectedCitation, setSelectedCitation],
  );

  const handleCloseCitation = useCallback(() => {
    if (setSelectedCitation) {
      setSelectedCitation(null);
    }
  }, [setSelectedCitation]);

  const hasError = initializationError || error || chatError;

  return (
    <div className="flex h-full flex-col rounded-xl bg-white shadow-sm ring-1 ring-zinc-200/60 overflow-hidden">
      {/* Messages */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {isInitializing && renderedMessages.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-zinc-300" />
          </div>
        ) : (
          <Conversation className="h-full">
            <ConversationContent className="gap-4">
              {renderedMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-8 py-12">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-zinc-100 to-zinc-50 flex items-center justify-center mb-4 ring-1 ring-zinc-200/60">
                    <Sparkles className="h-4 w-4 text-zinc-400" />
                  </div>
                  <p className="text-[13px] font-medium text-zinc-600">
                    Ask Agent Chat V2 to help with your draft
                  </p>
                  <p className="text-[11px] text-zinc-400 mt-1 max-w-[200px]">
                    Research, write sections, or rewrite content with AI assistance
                  </p>
                </div>
              ) : (
                renderedMessages.map((message) => (
                  <Message key={message.id} from={message.role}>
                    <MessageContent
                      className={cn(
                        message.role === 'assistant' ? 'max-w-full' : 'max-w-[85%]',
                      )}
                    >
                      {message.parts.map((part, index) => {
                        if (part.type === 'text') {
                          if (setSelectedCitation) {
                            return (
                              <div
                                key={`${message.id}-text-${index}`}
                                className="overflow-x-auto [&_.tableWrapper]:max-w-full [&_.tableWrapper]:overflow-x-auto [&_.tableWrapper>table]:min-w-[720px]"
                              >
                                <MessageContentViewer
                                  content={part.text}
                                  isStreaming={isStreaming && message.role === 'assistant'}
                                  extensions={[
                                    Citations.configure({
                                      matterId,
                                      threadId,
                                    }),
                                  ]}
                                />
                              </div>
                            );
                          }

                          return (
                            <MessageResponse key={`${message.id}-text-${index}`}>
                              {stripCitationTags(part.text)}
                            </MessageResponse>
                          );
                        }

                        if (part.type === 'reasoning') {
                          return (
                            <Reasoning
                              key={`${message.id}-reasoning-${index}`}
                              isStreaming={part.state === 'streaming'}
                            >
                              <ReasoningTrigger />
                              <ReasoningContent>{part.text}</ReasoningContent>
                            </Reasoning>
                          );
                        }

                        if (isToolPart(part)) {
                          return (
                            <div key={`${message.id}-tool-${index}`}>
                              {renderToolPart(
                                part,
                                isStreaming,
                                matterId,
                                fileNamesByShortId,
                                tasksById,
                              )}
                            </div>
                          );
                        }

                        const filePart = renderFilePart(part, `${message.id}-file-${index}`);
                        if (filePart) {
                          return filePart;
                        }

                        return null;
                      })}
                      {message.parts.length === 0 && isStreaming && (
                        <MessageResponse>...</MessageResponse>
                      )}
                    </MessageContent>
                  </Message>
                ))
              )}
            </ConversationContent>
          </Conversation>
        )}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 p-3 pt-0">
          {hasError && (
            <div className="flex items-start gap-2 px-1 pb-2">
              <p className="text-[11px] text-red-500 flex-1">
                {initializationError ?? error ?? chatError?.message}
              </p>
              <button
                type="button"
                onClick={handleForceReset}
                disabled={isResetting}
                className="flex items-center gap-1 shrink-0 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                title="Force-reset this thread by killing stuck workflows"
              >
                {isResetting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <RotateCcw className="h-3 w-3" />
                )}
                Reset Thread
              </button>
            </div>
          )}
        {isStreaming && (
          <div className="mb-2.5 rounded-lg border border-blue-200 bg-blue-50/80 px-2.5 py-2">
            <div className="flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
              <p className="text-[11px] font-medium text-blue-800 animate-pulse">
                {AGENT_CHAT_V2_THINKING_MESSAGES[thinkingMessageIndex]}...
              </p>
            </div>
          </div>
        )}
        {pendingImages.length > 0 && (
          <div className="mb-2.5 flex flex-wrap gap-2">
            {pendingImages.map((image) => (
              <div
                key={image.id}
                className="relative h-14 w-14 overflow-hidden rounded-md ring-1 ring-zinc-200"
              >
                <img
                  src={image.previewUrl}
                  alt={image.file.name}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePendingImage(image.id)}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                  aria-label={`Remove ${image.file.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="relative rounded-xl bg-zinc-50 ring-1 ring-zinc-200/80 focus-within:ring-zinc-300 focus-within:bg-white transition-all duration-150">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(event) => {
              if (event.target.files) {
                addImageFiles(event.target.files);
              }
              event.target.value = '';
            }}
          />
          <textarea
            ref={textareaRef}
            className="w-full resize-none bg-transparent px-3.5 py-2.5 pr-20 text-[13px] leading-relaxed outline-none placeholder:text-zinc-400"
            placeholder="Ask Agent Chat V2..."
            rows={1}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleInputKeyDown}
            onPaste={handleInputPaste}
            disabled={isStreaming || isInitializing}
          />
          <div className="absolute right-2 bottom-2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="h-6 w-6 flex items-center justify-center rounded-lg bg-zinc-200/70 text-zinc-600 hover:bg-zinc-300 transition-colors"
              aria-label="Add image attachment"
              disabled={isStreaming || isInitializing}
            >
              <ImagePlus className="h-3.5 w-3.5" />
            </button>
            {isStreaming && (activeRunId || status === 'submitted') ? (
              <button
                type="button"
                onClick={handleCancel}
                className="h-6 w-6 flex items-center justify-center rounded-lg bg-zinc-200 text-zinc-600 hover:bg-zinc-300 transition-colors"
              >
                <Square className="h-3 w-3" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={(!input.trim() && pendingImages.length === 0) || isInitializing}
                className={cn(
                  'h-6 w-6 flex items-center justify-center rounded-lg transition-all duration-150',
                  input.trim() || pendingImages.length > 0
                    ? 'bg-zinc-900 text-white hover:bg-zinc-800 shadow-sm'
                    : 'bg-zinc-200/60 text-zinc-400',
                )}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px] text-zinc-400 text-center mt-1.5">
          Enter to send • Shift+Enter for newline • Paste or upload images
        </p>
      </div>
      {selectedCitation ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              handleCloseCitation();
            }
          }}
        >
          <DialogContent className="max-w-4xl w-[90vw] h-[85vh] p-0 overflow-hidden z-1000">
            <button
              onClick={handleCloseCitation}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="Close citation viewer"
              type="button"
            >
              <X size={18} />
            </button>
            <CitationViewer
              url={selectedCitation.materializedCitation.url}
              citations={selectedCitation.materializedCitation.blocks || []}
              selectedCitationId={selectedCitation.selectedBlockId}
              setSelectedCitationId={setSelectedCitationId}
              shouldRender
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
};

// Backward-compatible alias for existing imports.
export const FinchChatCard = AgentChatV2Card;
export type FinchChatThreadState = AgentChatV2ThreadState;
